import Auth from '@aws-amplify/auth';
import { FederatedSignInOptions } from '@aws-amplify/auth/lib-esm/types';
import { GandalfUser, IdProvider } from '../types/auth';
import providersService from '../services/providers';
import metricsService from '../services/metrics';
import logger from '../utils/logger';
import { AppConfig } from '../types/app';
import { NONCE_ENTROPY_BYTES } from '../constants/nonce';
import { NonceGenerator } from '../contexts/NonceGeneratorContextProvider';
import { StateStorage } from '../contexts/StateStorageContextProvider';
import {
  CLOSE_POPUP_MS,
  IDP_URL_PARAM,
  LOGIN_HINT_PARAM,
  AUTH_FACTOR_HINT_PREFIX,
} from '../constants/auth';
import { rumService } from '../services/rum';
import { IdPs, isEltiProvider } from '../constants/providers';
import { setAmplifyConfig } from '../authConfig/amplify';
import { encryptLoginHint } from './encryptLoginHint';
import { LAST_APN_PROVIDER } from '../constants/storage';

function getClientIdFromUrl(): string {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('client_id') || 'NA';
}

function publishAuthMetrics(
  provider: IdProvider,
  namespace: string,
  user?: GandalfUser
) {
  const clientId = getClientIdFromUrl();
  const metricsPublisher = metricsService.getPublisher(namespace);
  metricsPublisher.publishCounterMonitor('SignIn', 1);
  metricsPublisher.publishCounterMonitor(`IdP:${provider.idp}`, 1);
  metricsPublisher.publishCounterMonitor(`ClientId:${clientId}`, 1);
  if (namespace === 'PostAuth') {
    rumService.recordEvent('idp_signIn', {
      IdP: provider,
      ClientId: clientId,
      VibeId: user?.vibeId,
      HatId: user?.hatId,
    });
  }
}

/**
 * Build a URL-safe, base64-encoded nonce.
 */
export const buildNonce = (generator: NonceGenerator): string => {
  const data = new ArrayBuffer(NONCE_ENTROPY_BYTES);
  generator.generateNonce(data);
  return btoa(
    Array.from(new Uint8Array(data))
      .map((byte) => String.fromCharCode(byte))
      .join('')
  );
};

/**
 * Start the auth flow by signing in using the Gandalf app client.
 */
export function startAuthFlow({
  provider,
  config,
  storage,
  nonceGenerator,
  loginHint,
  authFactorHint,
}: {
  provider: IdProvider;
  config: AppConfig;
  storage: StateStorage;
  nonceGenerator: NonceGenerator;
  loginHint?: string;
  authFactorHint?: string;
}) {
  // Use Amplify's urlOpener config to append login_hint as a top-level query param.
  // Cognito only forwards standard OIDC params (login_hint) to the IdP — custom params
  // like auth_factor_hint are stripped. We encode auth_factor_hint inside login_hint
  // using a prefix convention: "authfactor:<provider>" vs plain email.
  // The SHIM parses the prefix to determine which ISAC field to populate.
  let urlOpener:
    | ((url: string, redirectUrl: string) => Promise<any>)
    | undefined;
  if (
    config.enableBIDLoginHintPrefill === 'true' &&
    provider.idp === IdPs.AWSBuilderID &&
    (loginHint || authFactorHint)
  ) {
    urlOpener = async (url: string, redirectUrl: string) => {
      const parsed = new URL(url);
      if (authFactorHint) {
        // Social login path: encode as "authfactor:<provider>" (no encryption needed)
        parsed.searchParams.set(
          LOGIN_HINT_PARAM,
          `${AUTH_FACTOR_HINT_PREFIX}${authFactorHint}`
        );
        rumService.recordEvent('AuthFactorHintSent', {
          provider: authFactorHint,
        });
      } else if (loginHint && config.builderIdShimUrl) {
        // Email path: encrypt via SHIM API before placing in URL
        const token = await encryptLoginHint(
          loginHint,
          config.builderIdShimUrl
        );
        if (token) {
          parsed.searchParams.set(LOGIN_HINT_PARAM, token);
          rumService.recordEvent('LoginHintSent', {});
        }
        // If encryption failed (token is null), proceed without login_hint — graceful degradation
      }
      const windowProxy = window.open(parsed.toString(), '_self');
      return windowProxy ? Promise.resolve(windowProxy) : Promise.reject();
    };
  }

  setAmplifyConfig(config, provider, urlOpener);

  if (
    provider.idp !== IdPs.GandalfSession &&
    !isEltiProvider(provider.idpType)
  ) {
    providersService.setLastUsedProvider(provider);
  }

  const urlParams = new URLSearchParams(window.location.search);
  const clientId = urlParams.get('client_id') || 'NA';
  rumService.recordEvent('PreAuth', {
    IdP: provider,
    ClientId: clientId,
  });
  publishAuthMetrics(provider, 'PreAuth');

  const nonce = buildNonce(nonceGenerator);
  storage.setItem(nonce, JSON.stringify(provider));

  if (provider.idp === IdPs.AWSPartnerNetwork) {
    localStorage.setItem(LAST_APN_PROVIDER, JSON.stringify(provider));
  }

  return Auth.federatedSignIn({
    provider: provider.idp,
    customState: nonce,
  } as FederatedSignInOptions).catch((error) => {
    logger.debug('Failed to do federatedSignIn.', error);
  });
}

/**
 * Redirect to sign in using the LXP app client.
 */
export function redirectToLXPAuth(provider: IdProvider, user?: GandalfUser) {
  publishAuthMetrics(provider, 'PostAuth', user);
  const urlParams = new URLSearchParams(window.location.search);
  const clientId = urlParams.get('client_id') || 'NA';
  rumService.recordEvent('PostAuth', {
    IdP: provider,
    ClientId: clientId,
    VibeId: user?.vibeId,
    HatId: user?.hatId,
  });
  window.location.assign(provider.url);
}

export function replayAuth(provider: string, parameters: URLSearchParams) {
  parameters.set(IDP_URL_PARAM, provider);
  window.open(
    `${window.location.protocol}//${
      window.location.host
    }/login?${parameters.toString()}`,
    '_self',
    'noopener,noreferrer'
  );
}

/**
 * Log out from the Gandalf SignIn client.
 * See docs: https://docs.aws.amazon.com/cognito/latest/developerguide/logout-endpoint.html
 */
export function getLogoutUrl(config: AppConfig) {
  const redirectUrl = `${window.location.origin}/login`;
  return `https://${config.gandalfDomain}/logout?client_id=${config.authClientId}&logout_uri=${redirectUrl}`;
}

/**
 * Need to sign the user out when not authorized because the session is still active.
 * Not doing this will not allow them to change the email for ex. OTP.
 * @param config
 * @param setHasLoggedOut
 */
export function handleLogoutOut(
  config: AppConfig,
  setHasLoggedOut: React.Dispatch<React.SetStateAction<boolean>>
) {
  const logoutUrl = getLogoutUrl(config);
  const windowSettings = `toolbar=no,
      location=no,
      status=no,
      menubar=no,
      scrollbars=yes,
      resizable=yes,
      width=300,
      height=300,
      noopener,
      noreferrer`;
  const windowReference = window.open(logoutUrl, '_blank', windowSettings);
  setTimeout(() => {
    if (!windowReference || windowReference.closed) return;
    setHasLoggedOut(true);
    windowReference.close();
  }, CLOSE_POPUP_MS);
}
